// src/components/table/TransactionDetailModal.tsx
import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    Avatar,
    Paper,
    Grid,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PaymentIcon from "@mui/icons-material/Payment";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useContext } from "react";
import { DarkModeContext } from "../../context/darkModeContext";

interface Transaction {
    id: number;
    codigoFactura: string;
    cliente: string;
    fecha: string;
    total: number;
    tipoPago: string;
    estado: string;
    articulos: string[];
}

interface TransactionDetailModalProps {
    transaction: Transaction | null;
    open: boolean;
    onClose: () => void;
}

// Tema profesional para el modal simplificado
const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
        style: 'currency',
        currency: 'PEN',
        minimumFractionDigits: 2
    }).format(amount);
};

const formatDate = (dateString: string) => {
    try {
        const date = new Date(dateString);
        return format(date, "d 'de' MMMM 'de' yyyy, HH:mm", { locale: es });
    } catch {
        return dateString;
    }
};

const getStatusChip = (estado: string) => {
    const normalized = estado.toLowerCase();
    let color: "success" | "warning" | "error" | "default" = "default";
    if (normalized.includes("pagada") || normalized.includes("finalizada")) color = "success";
    else if (normalized.includes("pendiente")) color = "warning";
    else if (normalized.includes("anulada") || normalized.includes("cancelada")) color = "error";

    return (
        <Chip
            label={estado}
            color={color}
            size="small"
            sx={{ fontWeight: 600 }}
        />
    );
};

export const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
    transaction,
    open,
    onClose,
}) => {
    const { darkMode } = useContext(DarkModeContext);

    if (!transaction) return null;

    return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        // Añade esto si quieres forzar el fondo del Paper en modo oscuro
                        bgcolor: darkMode ? '#1e293b' : 'white',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor: "primary.main",
                        color: "white",
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)" }}>
                            <ReceiptIcon />
                        </Avatar>
                        <Typography variant="h6" fontWeight={600}>
                            Factura {transaction.codigoFactura}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: "white" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        {/* Información General */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Información General
                                </Typography>
                                <Box display="flex" flexDirection="column" gap={1.5}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CalendarTodayIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">
                                            <strong>Fecha:</strong> {formatDate(transaction.fecha)}
                                        </Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={1}>
                                        <PersonIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">
                                            <strong>Cliente:</strong> {transaction.cliente}
                                        </Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={1}>
                                        <PaymentIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">
                                            <strong>Método de pago:</strong> {transaction.tipoPago}
                                        </Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={1}>
                                        <ReceiptIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">
                                            <strong>Total:</strong> {formatCurrency(transaction.total)}
                                        </Typography>
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2">
                                            <strong>Estado:</strong> {getStatusChip(transaction.estado)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Lista de Artículos */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                                    <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>
                                        <ShoppingCartIcon fontSize="small" />
                                    </Avatar>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        Artículos ({transaction.articulos.length})
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 1.5 }} />
                                {transaction.articulos.length > 0 ? (
                                    <List dense>
                                        {transaction.articulos.map((articulo, index) => (
                                            <ListItem key={index} sx={{ py: 0.5 }}>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body2" fontWeight={500}>
                                                            • {articulo}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" textAlign="center">
                                        No hay artículos registrados
                                    </Typography>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
    );
};